import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useDeviceIdContext } from '@/components/device-id-provider';
import { ModuleSelectionCard } from './components/ModuleSelectionCard';
import { ModeSelectionDialog } from './components/ModeSelectionDialog';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppApproach } from '@/contexts/AppModuleContext';
import {
  Clock,
  Calendar,
  ArrowRight,
  Plus,
  MessageCircle,
  CheckCircle2,
  Brain
} from 'lucide-react';

// Types
interface Module {
  id: string;
  title: string;
  icon: string;
  description: string;
  type: 'main' | 'suggested';
  progress?: number;
  lastAccessed?: string;
  sessionsCount?: number;
}

interface DailyPractice {
  id: string;
  title: string;
  completed: boolean;
  dueDate: string;
}

// Constants
const STORAGE_KEYS = {
  DAILY_PRACTICES: 'hupo-daily-practices',
} as const;

// Utilities
const getLocalStorage = <T,>(key: string, defaultValue: T): T => {
  const stored = localStorage.getItem(key);
  if (!stored) return defaultValue;
  try {
    return JSON.parse(stored) as T;
  } catch (error) {
    console.error('Error parsing stored value:', error);
    return defaultValue;
  }
};

export function AppHomepage() {
  const navigate = useNavigate();
  const [selectedModule, setSelectedModule] = useState<string | null>(null);
  const [showModeDialog, setShowModeDialog] = useState(false);

  // Get device ID for API requests
  const { deviceId } = useDeviceIdContext();

  // Get current coaching approach
  const { approachConfig } = useAppApproach();

  // Use modules from the current approach configuration
  const modules: Module[] = approachConfig.growthAreas.map((area, index) => ({
    ...area,
    // Add mock progress data for demo purposes
    progress: index === 0 ? 78 : index === 1 ? 34 : 12,
    lastAccessed: index === 0 ? '2 days ago' : index === 1 ? '1 week ago' : '3 weeks ago',
    sessionsCount: index === 0 ? 12 : index === 1 ? 5 : 2,
  }));


  // Daily practices data with localStorage persistence - keyed by approach
  const storageKey = `${STORAGE_KEYS.DAILY_PRACTICES}-${approachConfig.id}`;
  const [dailyPractices, setDailyPractices] = useState<DailyPractice[]>(() =>
    getLocalStorage(storageKey, approachConfig.dailyPractices),
  );

  // Persist practices to localStorage when they change
  useEffect(() => {
    localStorage.setItem(
      storageKey,
      JSON.stringify(dailyPractices),
    );
  }, [dailyPractices, storageKey]);

  // Update daily practices when approach config changes
  useEffect(() => {
    // Check if the stored practices are different from config
    const storedPractices = getLocalStorage(storageKey, approachConfig.dailyPractices);
    const configPracticeIds = approachConfig.dailyPractices.map(p => p.id);
    const storedPracticeIds = storedPractices.map(p => p.id);

    // If the practice IDs don't match, use the config practices
    if (JSON.stringify(configPracticeIds) !== JSON.stringify(storedPracticeIds)) {
      setDailyPractices(approachConfig.dailyPractices);
    }
  }, [approachConfig.dailyPractices, storageKey]);

  // Use reflection question from current approach
  const REFLECTION_QUESTION = approachConfig.reflectionQuestion;

  // Event handlers
  const handleModuleSelect = (moduleId: string): void => {
    setSelectedModule(moduleId);
    setShowModeDialog(true);
  };

  const handleModeSelect = (mode: 'text' | 'voice' | 'video'): void => {
    setShowModeDialog(false);
    const moduleId = selectedModule;

    const routeMap: Record<string, string> = {
      text: `/app/chat?module=${moduleId}`,
      voice: `/app/voice?module=${moduleId}`,
      video: `/app/video?module=${moduleId}`,
    };

    navigate(routeMap[mode]);
  };

  const handleCloseDialog = (): void => {
    setShowModeDialog(false);
  };

  const togglePracticeCompletion = (practiceId: string): void => {
    setDailyPractices((prevPractices) => {
      const updatedPractices = prevPractices.map((practice) =>
        practice.id === practiceId
          ? { ...practice, completed: !practice.completed }
          : practice,
      );

      // Sort so completed items go to the bottom with animation
      return updatedPractices.sort((a, b) => {
        if (a.completed === b.completed) return 0;
        return a.completed ? 1 : -1;
      });
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Enhanced Header */}
      <header className="sticky top-0 z-20 bg-white border-b border-gray-200 py-4 px-6">
        <div className="flex justify-between items-center max-w-7xl mx-auto">
          <div className="flex items-center space-x-4">
            <div className="hidden sm:flex items-center space-x-2 text-sm text-gray-600">
              <Clock className="w-4 h-4" />
              <span>Good afternoon, Mir</span>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="default"
              className="bg-primary hover:bg-primary/90 text-white"
              onClick={() => {
                setSelectedModule('general-coaching');
                setShowModeDialog(true);
              }}
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Start Session
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Welcome and Stats Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-6 sm:mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 sm:mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                {approachConfig.welcomeTitle}
              </h1>
              <p className="text-base sm:text-lg text-gray-600">
                {approachConfig.welcomeSubtitle}
              </p>
            </div>
          </div>

        </motion.section>

        {/* Coaching Modules Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mb-6 sm:mb-8"
        >
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Your Growth Areas</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {modules.map((module, index) => (
              <motion.div
                key={module.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200 hover:border-primary/30 hover:shadow-md transition-all duration-200 cursor-pointer group h-full flex flex-col"
                onClick={() => handleModuleSelect(module.id)}
              >
                <div className="flex items-start justify-between mb-3 sm:mb-4">
                  <div className="text-2xl sm:text-3xl">{module.icon}</div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      module.type === 'main'
                        ? 'bg-primary/10 text-primary'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {module.type === 'main' ? 'Primary' : 'Suggested'}
                    </span>
                    <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-primary transition-colors" />
                  </div>
                </div>

                <h3 className="text-base sm:text-lg font-semibold text-gray-900 group-hover:text-primary mb-2 sm:mb-3 leading-tight transition-colors">
                  {module.title}
                </h3>

                <p className="text-sm text-gray-600 line-clamp-3 flex-grow">
                  {module.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Bottom Grid: Daily Practices and Reflection */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Daily Practices */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            className="bg-white rounded-xl border border-gray-200 hover:border-gray-300 transition-colors p-4 sm:p-6 h-full"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Daily Practices</h2>
            </div>

            <div className="space-y-3">
              <AnimatePresence mode="popLayout">
                {dailyPractices.map((practice, index) => (
                  <motion.div
                    key={practice.id}
                    layout
                    initial={{ opacity: 0, y: -10 }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      transition: {
                        type: "spring",
                        stiffness: 500,
                        damping: 30,
                        delay: index * 0.05
                      }
                    }}
                    exit={{
                      opacity: 0,
                      scale: 0.8,
                      transition: { duration: 0.2 }
                    }}
                    className={`group relative overflow-hidden ${
                      practice.completed ? 'mb-2' : ''
                    }`}
                  >
                    <motion.div
                      layout
                      className={`flex items-start space-x-3 p-3 rounded-lg border transition-all duration-300 ${
                        practice.completed
                          ? 'bg-gray-50 border-gray-300'
                          : 'bg-white border-gray-200 hover:border-primary/20'
                      }`}
                      animate={{
                        scale: practice.completed ? 0.98 : 1,
                      }}
                    >
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => togglePracticeCompletion(practice.id)}
                        className={`flex-shrink-0 w-5 h-5 rounded-full border-2 transition-all duration-300 relative ${
                          practice.completed
                            ? 'bg-primary border-primary'
                            : 'border-gray-300 hover:border-primary/50 bg-white'
                        }`}
                      >
                        <AnimatePresence>
                          {practice.completed && (
                            <motion.div
                              initial={{ scale: 0, rotate: -180 }}
                              animate={{ scale: 1, rotate: 0 }}
                              exit={{ scale: 0, rotate: 180 }}
                              transition={{ type: "spring", stiffness: 300, damping: 20 }}
                              className="absolute inset-0 flex items-center justify-center"
                            >
                              <CheckCircle2 className="w-5 h-5 text-white" />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.button>

                      <div className="flex-1 min-w-0">
                        <motion.p
                          className={`text-sm font-medium leading-relaxed transition-all duration-300 ${
                            practice.completed
                              ? 'line-through text-gray-500'
                              : 'text-gray-900'
                          }`}
                          animate={{
                            opacity: practice.completed ? 0.6 : 1,
                          }}
                        >
                          {practice.title}
                        </motion.p>
                      </div>
                    </motion.div>

                    {/* Completion animation overlay */}
                    <AnimatePresence>
                      {practice.completed && (
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: "100%" }}
                          exit={{ width: 0 }}
                          transition={{ duration: 0.5, ease: "easeOut" }}
                          className="absolute left-0 top-0 bottom-0 bg-primary/5 pointer-events-none"
                        />
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </motion.section>


          {/* Enhanced Reflection */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.9 }}
            className="bg-white rounded-xl border border-gray-200 hover:border-primary/20 transition-colors p-4 sm:p-6 h-full flex flex-col"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Daily Reflection</h2>
              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                <Brain className="w-4 h-4 text-gray-600" />
              </div>
            </div>

            <div className="text-center mb-4 sm:mb-6 flex-grow flex flex-col justify-center">
              <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">💭</div>
              <blockquote className="text-base sm:text-lg italic text-gray-700 leading-relaxed font-light">
                "{REFLECTION_QUESTION}"
              </blockquote>
            </div>

            <div className="mt-auto">
              <Button
                className="w-full bg-primary hover:bg-primary/90 text-white"
                onClick={() => {
                  setSelectedModule('reflection');
                  setShowModeDialog(true);
                }}
              >
                <Brain className="w-4 h-4 mr-2" />
                Start Reflecting
              </Button>
            </div>
          </motion.section>
        </div>
      </main>

      {/* Enhanced Floating Action Button */}
      <AnimatePresence>
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          transition={{ delay: 1.2, type: "spring", stiffness: 300, damping: 30 }}
          className="fixed bottom-6 right-6 lg:hidden z-20"
        >
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="w-16 h-16 rounded-full bg-primary text-white shadow-lg flex items-center justify-center group"
            onClick={() => {
              setSelectedModule('general-coaching');
              setShowModeDialog(true);
            }}
          >
            <MessageCircle className="w-6 h-6 group-hover:scale-110 transition-transform duration-200" />
          </motion.button>

          {/* Subtle pulse animation */}
          <div className="absolute inset-0 rounded-full bg-primary animate-ping opacity-20"></div>
        </motion.div>
      </AnimatePresence>

      {/* Mode selection dialog */}
      <ModeSelectionDialog
        isOpen={showModeDialog}
        onClose={handleCloseDialog}
        onSelectMode={handleModeSelect}
        moduleId={selectedModule || 'general-coaching'}
      />
    </div>
  );
}
